import type { LiveConversationStateT } from '@chatbot/shared-types';
import { useState } from 'react';

interface Props {
  state: LiveConversationStateT;
  isOwnAdmin: boolean;
  onTake: () => Promise<void>;
  onRelease: () => Promise<void>;
  onResolve: () => Promise<void>;
  onReturnToBot: () => Promise<void>;
}

export function ConversationActions({
  state,
  isOwnAdmin,
  onTake,
  onRelease,
  onResolve,
  onReturnToBot,
}: Props) {
  const [busy, setBusy] = useState(false);

  const wrap = (fn: () => Promise<void>) => async () => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // `bot_active` incluido a propósito: el operador entra cuando quiere, sin
  // esperar a que el bot escale. Con el flujo médico —que está guionado y
  // nunca escala— este botón no aparecía nunca.
  if (state === 'pending_human' || state === 'bot_active') {
    return (
      <button
        type="button"
        onClick={wrap(onTake)}
        disabled={busy}
        className={state === 'bot_active' ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
        title={
          state === 'bot_active'
            ? 'El bot la está atendiendo. Al tomarla, el bot deja de responder.'
            : 'Esta persona está esperando a un humano'
        }
      >
        {busy ? 'Procesando…' : state === 'bot_active' ? 'Intervenir' : 'Tomar'}
      </button>
    );
  }

  if (state === 'human_active' && isOwnAdmin) {
    return (
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={wrap(onReturnToBot)}
          disabled={busy}
          className="btn btn-secondary btn-sm"
          title="Bot retoma el control sin marcar como resuelta"
        >
          {busy ? 'Procesando…' : '🤖 Devolver al bot'}
        </button>
        <button
          type="button"
          onClick={wrap(onRelease)}
          disabled={busy}
          className="btn btn-secondary btn-sm"
          title="Vuelve a la cola para que otro operador la tome"
        >
          {busy ? 'Procesando…' : 'Devolver al pool'}
        </button>
        <button
          type="button"
          onClick={wrap(onResolve)}
          disabled={busy}
          className="btn btn-primary btn-sm"
          title="Marca la conversación como cerrada"
        >
          {busy ? 'Procesando…' : 'Resolver'}
        </button>
      </div>
    );
  }

  return null;
}
